import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, Users } from "lucide-react";

/**
 * UserSelector — pill-checkbox UI for selecting specific users an asset targets.
 * Value is a comma-separated string of user ids OR an array.
 * `onChange(nextString, nextArray)` — receives both formats.
 */
export default function UserSelector({ value = "", onChange, testIdPrefix = "user-access" }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    // Retry up to 2 times with backoff — Cloudflare edge occasionally returns 502.
    let lastErr = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/admin/users");
        setUsers(data.filter((u) => u.role !== "admin"));
        setLoading(false);
        return;
      } catch (e) {
        lastErr = e;
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
    }
    setError(lastErr?.response?.status ? `HTTP ${lastErr.response.status}` : "Network error");
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const parsed = Array.isArray(value)
    ? value
    : String(value || "").split(",").map((s) => s.trim()).filter(Boolean);

  function toggle(id) {
    const set = new Set(parsed);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    const arr = Array.from(set);
    onChange && onChange(arr.join(", "), arr);
  }

  function toggleAll() {
    if (parsed.length === users.length) {
      onChange && onChange("", []);
    } else {
      const arr = users.map((u) => u.id);
      onChange && onChange(arr.join(", "), arr);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground flex items-center gap-2">
          <Users className="h-3 w-3" /> User Access
        </label>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
            {parsed.length === 0 ? "All (per location)" : `${parsed.length} selected`}
          </span>
          {users.length > 0 && (
            <button type="button" onClick={toggleAll} className="font-mono text-[9px] uppercase tracking-widest underline underline-offset-2 text-muted-foreground hover:text-foreground transition" data-testid={`${testIdPrefix}-toggle-all`}>
              {parsed.length === users.length ? "Clear" : "All"}
            </button>
          )}
        </div>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground py-3">Loading users…</div>
      ) : error ? (
        <div className="text-xs py-3 flex items-center gap-3">
          <span className="text-[#E15C2C]">Couldn&apos;t load users ({error}).</span>
          <button
            type="button"
            onClick={fetchUsers}
            className="font-mono text-[10px] uppercase tracking-widest underline underline-offset-2 hover:text-foreground text-muted-foreground"
            data-testid={`${testIdPrefix}-retry`}
          >
            Retry
          </button>
        </div>
      ) : users.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2">No partner users yet — add users under Admin → Users.</div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-selector`}>
          {users.map((u) => {
            const active = parsed.includes(u.id);
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => toggle(u.id)}
                className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] border rounded-sm transition flex items-center gap-1.5 ${
                  active
                    ? "bg-foreground text-background border-foreground"
                    : "border-border hover:border-border-strong text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`${testIdPrefix}-${u.id}`}
              >
                {active && <Check className="h-3 w-3" />}
                {u.name}
              </button>
            );
          })}
        </div>
      )}
      <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        Leave empty to grant access based on Location only. When users are selected, only those users (plus anyone matching by Location) can see this asset.
      </p>
    </div>
  );
}
